// Previzualizarea unei soluții în panoul din dreapta al paginii Soluții.
//
// Până pe 26.09.2026 panoul arăta aceeași siluetă izometrică la orice rând —
// planul de situație, arhitectura și structura arătau identic, deci desenul nu
// spunea la CE te uiți. Acum fiecare disciplină se desenează cum o citești:
//
//   * PLANUL DE SITUAȚIE: de sus, amprentele corpurilor pe teren;
//   * ARHITECTURA: de sus, conturul fiecărui bloc, cu peretele de fațadă gros;
//   * STRUCTURA: volumul izometric (`Volum`), ca până acum.
//
// Sursa e aceeași siluetă (`summary.silueta`, conturul REAL al fiecărui nivel,
// v. `services/silueta.py`), deci nu se cere nimic în plus și nu se desenează
// nimic ce nu e în soluție: fără pereți interiori deduși, fără alei inventate
// (regula din 24.09.2026).

import type { Silueta } from "../api";
import Volum from "./Volum";

type Props = {
  disciplina: "site_plan" | "architecture" | "structure";
  cheie: string;
  silueta: Silueta | null;
  niveluri: number | null;
  w?: number;
  h?: number;
};

export default function PrevizSolutie({ disciplina, cheie, silueta, niveluri,
                                        w = 336, h = 170 }: Props) {
  const blocuri = (silueta?.corpuri ?? []).flatMap((c) => c.blocuri)
    .filter((b) => b.poly.length >= 3);
  if (disciplina === "structure" || !blocuri.length) {
    return <Volum cheie={cheie} niveluri={niveluri} silueta={silueta} w={w} h={h} />;
  }
  // amprenta de sus: cel mai de jos bloc suprateran al fiecărui corp dă
  // conturul; blocurile retrase de deasupra se desenează peste, mai subțire
  let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
  for (const b of blocuri) {
    for (const [x, y] of b.poly) {
      xmin = Math.min(xmin, x); xmax = Math.max(xmax, x);
      ymin = Math.min(ymin, y); ymax = Math.max(ymax, y);
    }
  }
  const lat = Math.max(xmax - xmin, 1e-6);
  const inalt = Math.max(ymax - ymin, 1e-6);
  const margine = disciplina === "site_plan" ? 22 : 16;
  const k = Math.min((w - 2 * margine) / lat, (h - 2 * margine) / inalt);
  const ox = (w - lat * k) / 2;
  const oy = (h - inalt * k) / 2;
  // y în sus în plan, în jos pe ecran
  const pct = (p: [number, number]) =>
    `${(ox + (p[0] - xmin) * k).toFixed(1)},${(h - oy - (p[1] - ymin) * k).toFixed(1)}`;
  const ordonate = [...blocuri].sort((a, b) => a.z - b.z);
  const sit = disciplina === "site_plan";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img"
         aria-label={sit ? "plan de situație" : "plan de nivel"} style={{ display: "block" }}>
      <rect width={w} height={h} fill={sit ? "#dcebd3" : "var(--app)"} />
      {ordonate.map((b, i) => (
        <polygon key={i} points={b.poly.map(pct).join(" ")}
                 fill="#ffffff" stroke="var(--ink)"
                 strokeWidth={sit ? 1.2 : b.z > 0 ? 1 : 2.4}
                 strokeLinejoin="round" />
      ))}
    </svg>
  );
}
